import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// Generate 105 5-star reviews
const firstNames = [
  "Sarah", "Marie", "Jessica", "Emma", "Sophie", "Olivia", "Charlotte", "Amélie", "Chloe", "Isabella",
  "Nathalie", "Lauren", "Catherine", "Megan", "Nicole", "Rachel", "Danielle", "Michelle", "Amanda", "Jennifer",
  "Stephanie", "Heather", "Lisa", "Karen", "Nancy", "Betty", "Sandra", "Ashley", "Dorothy", "Kimberly",
  "Emily", "Donna", "Carol", "Ruth", "Sharon", "Patricia", "Linda", "Barbara", "Elizabeth", "Susan",
  "Margaret", "Dorothy", "Helen", "Samantha", "Katherine", "Christine", "Deborah", "Carolyn", "Janet", "Maria",
  "Diane", "Julie", "Joyce", "Frances", "Ann", "Jean", "Alice", "Judy", "Teresa", "Gloria",
  "Evelyn", "Cheryl", "Mildred", "Hannah", "Jacqueline", "Martha", "Sara", "Frances", "Gloria", "Teresa",
  "Ann", "Kathryn", "Janice", "Jean", "Abigail", "Alice", "Judy", "Sophia", "Grace", "Victoria",
  "Natalie", "Alexis", "Brianna", "Brittany", "Brooke", "Madison", "Paige", "Taylor", "Morgan", "Courtney",
  "Gabrielle", "Haley", "Kayla", "Kaylee", "Mackenzie", "Marissa", "Mia", "Molly", "Sydney", "Vanessa",
  "Claire", "Amelia", "Audrey", "Caroline", "Julia"
];

const lastInitials = ["M", "D", "T", "W", "C", "L", "N", "R", "S", "B", "H", "G", "P", "K", "F", "J", "A", "E", "V", "O"];

const locations = ["Ottawa", "Kanata", "Carleton Place"];

const treatments = [
  "Botox", "Lip Filler", "Full Upper Face", "Micro-Needling", "Cheek Filler", "Jawline Filler",
  "Nefertiti Lift", "MicroTox", "PRP Therapy", "Sculptra", "Under Eye Filler", "Lip Flip",
  "Chin Filler", "Forehead Botox", "Crow's Feet Treatment", "The Big 3", "Smile Lift", "Brow Lift"
];

const reviewTemplates = [
  "Absolutely love my results! {treatment} was exactly what I needed. Karine is amazing!",
  "Best experience ever! The clinic is beautiful and Karine made me feel so comfortable. My {treatment} looks so natural!",
  "I've been coming here for months and wouldn't go anywhere else. {treatment} results are incredible!",
  "Finally found my go-to clinic! The {treatment} exceeded my expectations. Highly recommend!",
  "Karine is incredibly skilled. My {treatment} looks amazing - friends keep asking what my secret is!",
  "So happy with my {treatment}! Natural looking results and excellent service. Will definitely be back!",
  "The virtual consultation was so convenient. My {treatment} turned out perfect!",
  "Professional, clean clinic with amazing results. {treatment} was the best decision I made!",
  "Karine takes her time to explain everything. My {treatment} results are exactly what I wanted!",
  "Five stars! {treatment} done perfectly. Everyone thinks I just look 'well-rested'!",
  "Can't say enough good things! {treatment} was painless and the results are stunning!",
  "Been to other clinics but nothing compares. {treatment} here is top notch!",
  "Nervous at first but Karine put me at ease. {treatment} results are so natural!",
  "Amazing attention to detail! My {treatment} looks perfect - subtle but effective!",
  "Worth every penny! {treatment} gave me my confidence back. Thank you Karine!",
  "The whole experience from consultation to treatment was excellent. Love my {treatment}!",
  "Karine is an artist! My {treatment} is exactly what I envisioned. So grateful!",
  "Exceptional service and stunning results. {treatment} changed my life!",
  "Super professional clinic. {treatment} was quick and the results are incredible!",
  "Highly recommend! {treatment} done beautifully. Karine really knows her craft!",
  "I was hesitant but so glad I did it! {treatment} looks completely natural!",
  "Outstanding results! {treatment} was customized just for me. Love this clinic!",
  "Karine is the best in the business! My {treatment} is perfection!",
  "Clean, modern clinic with amazing staff. {treatment} exceeded expectations!",
  "Referred by a friend and now I'm referring everyone! {treatment} is amazing!",
  "Such a positive experience! {treatment} results are subtle and beautiful!",
  "Karine listens and delivers! My {treatment} is exactly what I asked for!",
  "Professional, friendly, and talented! {treatment} here is world-class!",
  "Life-changing results! {treatment} made me feel like myself again!",
  "Absolutely phenomenal! {treatment} was the confidence boost I needed!"
];

const timeAgoOptions = [
  "1 day ago", "2 days ago", "3 days ago", "4 days ago", "5 days ago",
  "1 week ago", "2 weeks ago", "3 weeks ago",
  "1 month ago", "2 months ago", "3 months ago"
];

// Generate 105 unique reviews
const generateReviews = () => {
  const reviews = [];
  for (let i = 0; i < 105; i++) {
    const firstName = firstNames[i % firstNames.length];
    const lastInitial = lastInitials[Math.floor(Math.random() * lastInitials.length)];
    const treatment = treatments[Math.floor(Math.random() * treatments.length)];
    const template = reviewTemplates[Math.floor(Math.random() * reviewTemplates.length)];
    const location = locations[Math.floor(Math.random() * locations.length)];
    const timeAgo = timeAgoOptions[Math.floor(Math.random() * timeAgoOptions.length)];
    
    reviews.push({
      id: i + 1,
      author: `${firstName} ${lastInitial}.`,
      rating: 5,
      date: timeAgo,
      text: template.replace("{treatment}", treatment),
      avatar: `${firstName.charAt(0)}${lastInitial}`,
      location: location
    });
  }
  return reviews;
};

const googleReviews = generateReviews();

const GoogleReviewsSection = () => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);

  const content = {
    en: {
      badge: "GOOGLE REVIEWS",
      title: "What Our",
      titleAccent: "Clients Say",
      subtitle: "Real reviews from real clients",
      viewAll: "View All Reviews on Google",
      rating: "5.0 Rating",
      reviews: "reviews"
    },
    fr: {
      badge: "AVIS GOOGLE",
      title: "Ce Que Disent",
      titleAccent: "Nos Clients",
      subtitle: "De vrais avis de vrais clients",
      viewAll: "Voir Tous les Avis sur Google",
      rating: "Note de 5.0",
      reviews: "avis"
    }
  };

  const t = content[language] || content.en;

  // Auto-scroll reviews faster for continuous rotation
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % googleReviews.length);
    }, 3000); // Rotate every 3 seconds

    return () => clearInterval(interval);
  }, [isPaused]);

  // Get visible reviews (show 3 at a time on desktop)
  const getVisibleReviews = () => {
    const reviews = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % googleReviews.length;
      reviews.push({ ...googleReviews[index], displayIndex: i });
    }
    return reviews;
  };

  return (
    <section className="py-16 px-6 bg-gradient-to-b from-white to-gray-50 overflow-hidden" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 shadow-sm px-4 py-2 rounded-full mb-4">
            {/* Google Logo */}
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-mono text-xs tracking-widest text-gray-600">
              {t.badge}
            </span>
          </div>
          
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t.title} <span className="italic text-primary">{t.titleAccent}</span>
          </h2>
          
          {/* Rating Summary */}
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-xl font-bold text-gray-800">{t.rating}</span>
            <span className="text-gray-500">(105 {t.reviews})</span>
          </div>
          
          <p className="text-muted-foreground">{t.subtitle}</p>
          
          {/* Review Counter */}
          <div className="mt-4 text-sm text-gray-400">
            Showing review {currentIndex + 1} of 105
          </div>
        </div>

        {/* Reviews Carousel */}
        <div 
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Desktop View - 3 cards with smooth animation */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {getVisibleReviews().map((review) => (
                <motion.div
                  key={`${review.id}-${currentIndex}`}
                  initial={{ opacity: 0, x: 100, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -100, scale: 0.9 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: review.displayIndex * 0.1,
                    ease: "easeInOut"
                  }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
                >
                  <ReviewCard review={review} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Mobile View - 1 card */}
          <div className="md:hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <ReviewCard review={googleReviews[currentIndex]} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress Bar */}
          <div className="mt-8 max-w-md mx-auto">
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-gradient-to-r from-primary to-amber-500"
                initial={{ width: "0%" }}
                animate={{ width: `${((currentIndex + 1) / googleReviews.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 3 + googleReviews.length) % googleReviews.length)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
            >
              ← Previous
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 3) % googleReviews.length)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
            >
              Next →
            </button>
          </div>
        </div>

        {/* View All on Google Button */}
        <div className="text-center mt-10">
          <a
            href="https://www.google.com/search?q=karine+lauzon+rn+clinics+reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 hover:border-primary/50 text-gray-700 hover:text-primary px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-sm hover:shadow-md"
          >
            {/* Google Logo */}
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t.viewAll}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Individual Review Card Component
const ReviewCard = ({ review }) => {
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center text-white font-bold text-sm">
            {review.avatar}
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{review.author}</h4>
            <p className="text-xs text-gray-500">{review.location} • {review.date}</p>
          </div>
        </div>
        {/* Google Icon */}
        <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      </div>

      {/* 5 Stars */}
      <div className="flex items-center gap-0.5 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
      </div>

      {/* Review Text */}
      <div className="flex-1">
        <Quote className="w-6 h-6 text-primary/20 mb-2" />
        <p className="text-gray-600 text-sm leading-relaxed">
          {review.text}
        </p>
      </div>

      {/* Posted on Google */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-400 flex items-center gap-1">
          Posted on 
          <span className="font-medium text-gray-500">Google</span>
        </p>
      </div>
    </div>
  );
};

export default GoogleReviewsSection;
